import { useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LabeledValue } from '@/lib/api';

/** Internal type that adds a stable React key to LabeledValue */
type KeyedLabeledValue = LabeledValue & { _key: string };

let _entryKeyCounter = 0;
function nextEntryKey(): string {
  return `entry-${++_entryKeyCounter}`;
}

/** Attach stable _key to LabeledValue entries that lack one */
function ensureKeys(entries: LabeledValue[]): KeyedLabeledValue[] {
  return entries.map((e) =>
    '_key' in e ? (e as KeyedLabeledValue) : { ...e, _key: nextEntryKey() },
  );
}

/** Strip _key before sending to parent / API */
function stripKeys(entries: KeyedLabeledValue[]): LabeledValue[] {
  return entries.map(({ value, label }) => ({ value, label }));
}

export function LabeledEntryList({
  entries,
  onChange,
  labels,
  defaultLabel,
  placeholder,
  fieldLabel,
  inputType = 'text',
  maxItems = 10,
}: {
  entries: LabeledValue[];
  onChange: (entries: LabeledValue[]) => void;
  labels: string[];
  defaultLabel: string;
  placeholder: string;
  fieldLabel: string;
  inputType?: string;
  maxItems?: number;
}) {
  const keyed = useMemo(() => ensureKeys(entries), [entries]);

  const updateEntry = (key: string, field: 'value' | 'label', val: string) => {
    const updated = keyed.map((e) => (e._key === key ? { ...e, [field]: val } : e));
    onChange(stripKeys(updated));
  };

  const removeEntry = (key: string) => {
    onChange(stripKeys(keyed.filter((e) => e._key !== key)));
  };

  const addEntry = () => {
    onChange(stripKeys([...keyed, { value: '', label: defaultLabel, _key: nextEntryKey() }]));
  };

  return (
    <div className="space-y-2">
      <Label>{fieldLabel}</Label>
      {keyed.map((entry) => (
        <div key={entry._key} className="flex items-center gap-2">
          <Select value={entry.label} onValueChange={(v) => updateEntry(entry._key, 'label', v)}>
            <SelectTrigger className="w-[110px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {labels.map((l) => (
                <SelectItem key={l} value={l}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type={inputType}
            value={entry.value}
            onChange={(e) => updateEntry(entry._key, 'value', e.target.value)}
            placeholder={placeholder}
            className="flex-1 h-9"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 flex-shrink-0"
            onClick={() => removeEntry(entry._key)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      {keyed.length < maxItems && (
        <Button type="button" variant="outline" size="sm" className="w-full" onClick={addEntry}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add {fieldLabel.replace(/s$/, '')}
        </Button>
      )}
    </div>
  );
}
